import posthog from 'posthog-js';
import { supabase } from './supabase';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

if (typeof window !== 'undefined' && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 PostHog initialized');
      }
    },
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage',
  });
}

// Detect device type from user agent
function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod/.test(ua)) return 'mobile';
  if (/ipad|tablet/.test(ua)) return 'tablet';
  return 'desktop';
}

// Geo lookup via ipapi.co — cached 24h in localStorage
interface GeoData { country: string; country_code: string; city: string; region: string; }
let _geoCache: GeoData | null = null;
async function getGeoData(): Promise<GeoData | null> {
  const CACHE_KEY = 'sl_geo_cache';
  const CACHE_TS  = 'sl_geo_ts';
  const TTL = 24 * 60 * 60 * 1000; // 24h
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const ts     = parseInt(localStorage.getItem(CACHE_TS) || '0');
    if (_geoCache) return _geoCache;
    if (cached && Date.now() - ts < TTL) {
      _geoCache = JSON.parse(cached);
      return _geoCache;
    }
    const res  = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const data = await res.json();
    const geo: GeoData = {
      country:      data.country_name || '',
      country_code: data.country_code || '',
      city:         data.city || '',
      region:       data.region || '',
    };
    _geoCache = geo;
    localStorage.setItem(CACHE_KEY, JSON.stringify(geo));
    localStorage.setItem(CACHE_TS, String(Date.now()));
    return geo;
  } catch {
    return null;
  }
}

// Get or create a session ID (resets after 30min inactivity)
function getSessionId(): string {
  const key = 'sl_session_id';
  const tsKey = 'sl_session_ts';
  const now = Date.now();
  const last = parseInt(localStorage.getItem(tsKey) || '0');
  const SESSION_TIMEOUT = 30 * 60 * 1000;

  if (!localStorage.getItem(key) || now - last > SESSION_TIMEOUT) {
    const id = `sess_${now}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(key, id);
  }
  localStorage.setItem(tsKey, String(now));
  return localStorage.getItem(key)!;
}

// Log event to Supabase visitor_logs (fire-and-forget)
async function logToSupabase(eventType: string, properties: Record<string, any> = {}) {
  try {
    const distinctId = posthog.get_distinct_id?.() || 'anonymous';
    const geo = await getGeoData();
    await supabase.from('visitor_logs').insert({
      event_type: eventType,
      distinct_id: distinctId,
      session_id: getSessionId(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      properties: { ...properties, ...(geo ? { geo } : {}) },
    });
  } catch {
    // Silently fail — never block the user experience
  }
}

export const analytics = {
  pageView: (path: string) => {
    posthog.capture('$pageview', { path });
    logToSupabase('$pageview', { path });
  },

  productViewed: (product: { id: string; name: string; price: number }) => {
    posthog.capture('product_viewed', {
      product_id: product.id,
      product_name: product.name,
      product_price: product.price,
    });
    logToSupabase('product_viewed', {
      product_id: product.id,
      product_name: product.name,
      product_price: product.price,
    });
  },

  addToCart: (product: { id: string; name: string; price: number; quantity: number }) => {
    posthog.capture('add_to_cart', {
      product_id: product.id,
      product_name: product.name,
      quantity: product.quantity,
      total_value: product.price * product.quantity,
    });
    logToSupabase('add_to_cart', {
      product_id: product.id,
      product_name: product.name,
      quantity: product.quantity,
      total_value: product.price * product.quantity,
    });
  },

  removeFromCart: (productId: string) => {
    posthog.capture('remove_from_cart', { product_id: productId });
    logToSupabase('remove_from_cart', { product_id: productId });
  },

  checkoutStarted: (total: number, items: number) => {
    posthog.capture('checkout_started', {
      cart_total: total,
      items_count: items,
    });
    logToSupabase('checkout_started', { cart_total: total, items_count: items });
  },

  purchaseCompleted: (orderId: string, total: number) => {
    posthog.capture('purchase', {
      order_id: orderId,
      revenue: total,
    });
    logToSupabase('purchase', { order_id: orderId, revenue: total });
  },

  contactFormSubmit: (name: string) => {
    posthog.capture('contact_form_submit', { form_name: name });
    logToSupabase('contact_form_submit', { form_name: name });
  },

  userLogin: (userId: string) => {
    posthog.identify(userId);
    posthog.capture('user_login');
    logToSupabase('user_login', { user_id: userId });
  },

  userSignup: (userId: string) => {
    posthog.identify(userId);
    posthog.capture('user_signup');
    logToSupabase('user_signup', { user_id: userId });
  },

  userLogout: () => {
    posthog.capture('user_logout');
    posthog.reset();
    logToSupabase('user_logout');
  },
};

export default posthog;
